/**
 * Plausible Analytics — privacy-first event tracking (no cookie).
 * Loaded only if `REACT_APP_PLAUSIBLE_DOMAIN` is configured.
 *
 * Strategic events tracked across the funnel :
 *   - signup_started / signup_completed
 *   - google_signin (Emergent OAuth redirect)
 *   - workspace_created
 *   - project_seeded (template)
 *   - invite_sent (collaborator invitation)
 *   - campus_viewed (1st campus visit)
 *   - checkout_started (Stripe redirect)
 *   - checkout_paid (Stripe success page)
 *   - apply_form_submitted (B2B leads)
 *   - diagnostic_completed
 */

const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
const PLAUSIBLE_HOST = process.env.REACT_APP_PLAUSIBLE_HOST || 'https://plausible.io';

let scriptInjected = false;

export function initPlausible() {
    if (!PLAUSIBLE_DOMAIN) return; // analytics disabled if no domain configured
    if (scriptInjected || typeof document === 'undefined') return;
    scriptInjected = true;

    // Tag manager pattern : push events to queue while script loads
    window.plausible = window.plausible || function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
    };

    const s = document.createElement('script');
    s.defer = true;
    s.setAttribute('data-domain', PLAUSIBLE_DOMAIN);
    s.src = `${PLAUSIBLE_HOST}/js/script.tagged-events.js`;
    document.head.appendChild(s);
}

/**
 * Track a custom event : Plausible (si configuré) + miroir first-party
 * vers /api/analytics/track (tunnel visible dans le Super Admin).
 * @param {string} eventName
 * @param {object} [props] — small flat object of string/number values
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function getAnonId() {
    try {
        let id = localStorage.getItem('av_anon_id');
        if (!id) {
            id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            localStorage.setItem('av_anon_id', id);
        }
        return id;
    } catch (_e) { return null; }
}

export function trackEvent(eventName, props) {
    if (typeof window === 'undefined') return;
    // 1) Plausible (optionnel)
    if (PLAUSIBLE_DOMAIN) {
        try {
            if (typeof window.plausible === 'function') {
                window.plausible(eventName, props ? { props } : undefined);
            }
        } catch (_e) { /* silent */ }
    }
    // 2) Miroir first-party (tunnel Super Admin) — fire-and-forget
    try {
        if (!API_BASE) return;
        fetch(`${API_BASE}/api/analytics/track`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event: eventName, anon_id: getAnonId(), props: props || null }),
        }).catch(() => { });
    } catch (_e) { /* silent */ }
}

/**
 * Estimate the reading time of an HTML snippet (in minutes), based on ~200 wpm.
 */
export function estimateReadingTime(html) {
    if (!html || typeof html !== 'string') return 0;
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = text ? text.split(' ').length : 0;
    return Math.max(1, Math.round(words / 200));
}

/**
 * Classify an outbound CTA click on a CMS page by its destination.
 * Returns null when the link is internal navigation we don't track here.
 */
export function classifyCmsCta(href) {
    if (!href) return null;
    try {
        const lower = href.toLowerCase();
        if (lower.startsWith('mailto:')) return 'mailto';
        if (lower.startsWith('tel:')) return 'tel';
        if (lower.startsWith('whatsapp:') || lower.includes('wa.me/')) return 'whatsapp';
        // Strategic internal CTAs
        if (lower.includes('/apply')) return 'apply';
        if (lower.includes('/donate')) return 'donate';
        if (lower.includes('/p/tarifs') || lower.includes('/p/pricing')) return 'tarifs';
        if (lower.includes('/p/diagnostic')) return 'diagnostic';
        if (lower.includes('/p/contact')) return 'contact';
        if (lower.includes('/p/campus') || lower.includes('/p/formation')) return 'campus';
        if (lower.includes('/p/api')) return 'api';
        if (lower.includes('/auth/signin')) return 'signin';
        // External outbound (different host)
        if (/^https?:\/\//.test(href)) {
            const host = new URL(href).host;
            if (host && typeof window !== 'undefined' && host !== window.location.host) return 'external';
        }
    } catch (_e) { /* malformed URL */ }
    return null;
}
